import React, { Fragment, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import Cookies from 'js-cookie';
import { t } from 'Src/i18n';
import { formatApiDate } from 'Utils/date/apiDate';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import type { TemplateValues } from 'Components/frameworks/template-string/TemplateString';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import type { PaymentFormValues, PlanSelectorComponent } from 'Src/types/payment';
import { Title } from 'Components/frameworks/title/Title';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { useFormikContext } from 'formik';
import { AsyncLoader } from 'Components/widgets/loading/async-loader/AsyncLoader';
import { SVRL_PMNTS } from 'Views/booking/BookingEngine.utils';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Modal } from 'Components/frameworks/modal/Modal';
import { Text } from 'Components/frameworks/text/Text';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { PaymentFormikInput } from 'Components/widgets/payment-info/paymentFormik';
import { castAs } from 'Src/types/cast-helpers';
export type Plan = {
  value: number | string;
  label: string;
  selector?: PlanSelectorComponent;
  name: string;
  template?: {
    values: TemplateValues;
    valuesClassName?: Record<string, string>;
  };
  isIframe?: boolean;
  withModalInformation?: boolean;
  modalTitle?: string;
  modalDescription?: string;
  onChange?: () => void;
  regulations?: ReactNode;
};
export type PaymentPlanProps = {
  depositSelectedLabel?: string;
  hasFullAmount?: boolean;
  isDisabled?: boolean;
  payFullAmountLabel?: string;
  paymentSchedule: PaymentSchedule;
  displayTooltip?: boolean;
  setHasFullAmount?: (arg0: boolean) => void;
  paymentPlans?: ReadonlyArray<Plan>;
  isLoading?: boolean;
};
export const PaymentPlan = ({
  depositSelectedLabel,
  hasFullAmount,
  isDisabled,
  payFullAmountLabel,
  paymentSchedule,
  setHasFullAmount,
  displayTooltip = true,
  paymentPlans = [],
  isLoading,
}: PaymentPlanProps) => {
  const { isFreeDeposit } = useContext(PaymentInfoContext);
  const appContext = useAppContext();
  const rePaymentSchedule = paymentSchedule.repaymentSchedules[0] ?? {
    amount: { formattedValue: '' },
    deadline: '',
  };
  const formik = useFormikContext<PaymentFormValues>();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isDefaultPlanEnabled =
    (!isFreeDeposit && paymentSchedule.repaymentSchedules.length > 0) || paymentSchedule.isUpsell;
  const payDepositRegulationsText = t('booking.banking.paymentSchedule.payDepositRegulations');
  const inThirtyMinutes = new Date(new Date().getTime() + 60 * 30 * 1000);
  useEffect(() => {
    !hasFullAmount &&
      Cookies.set(SVRL_PMNTS, 1, {
        expires: inThirtyMinutes,
      });
  }, []);

  if (!isDefaultPlanEnabled && !paymentPlans.length && !isLoading) {
    return null;
  }

  const totalPaymentPlan = {
    value: paymentSchedule.total?.value,
    label: payFullAmountLabel || 'booking.banking.paymentSchedule.payFullAmount',
    name: 'amountToPay',
    template: {
      values: {
        totalAmount: paymentSchedule.total?.formattedValue,
      },
      valuesClassName: {
        totalAmount: 'font-bold text-sienna',
      },
    },
    onChange: () => {
      setHasFullAmount && setHasFullAmount(true);
      Cookies.remove(SVRL_PMNTS);
    },
  };
  const depositPaymentPlan = {
    value: paymentSchedule.depositAmount.value,
    label: depositSelectedLabel || 'booking.banking.paymentSchedule.depositSelected',
    name: 'amountToPay',
    withModalInformation:
      isFeatureFlipped(appContext)('booking.banking.enableFreeDeposit') && displayTooltip,
    modalTitle: 'booking.banking.paymentSchedule.freeDeposit.title',
    modalDescription: 'booking.banking.paymentSchedule.freeDeposit.description',
    template: {
      values: {
        depositAmount: paymentSchedule.depositAmount?.formattedValue,
        balanceAmount: rePaymentSchedule.amount?.formattedValue,
        balanceDate: formatApiDate(rePaymentSchedule.deadline, t('booking.form.dateFormat')),
      },
      valuesClassName: {
        depositAmount: 'font-bold text-sienna',
        balanceAmount: 'font-bold text-sienna',
        balanceDate: 'font-bold',
      },
    },
    onChange: () => {
      setHasFullAmount && setHasFullAmount(false);
      Cookies.set(SVRL_PMNTS, 1, {
        expires: inThirtyMinutes,
      });
    },
    regulations: payDepositRegulationsText && (
      <p className="text-sm italic" data-testid="payDepositRegulationsText">
        {payDepositRegulationsText}
      </p>
    ),
  };
  const defaultPaymentPlans = castAs<Plan[]>(
    [
      isDefaultPlanEnabled || !!paymentPlans.length ? totalPaymentPlan : null,
      isDefaultPlanEnabled ? depositPaymentPlan : null,
    ].filter((plan) => plan != null && plan.value),
  );
  const plans: ReadonlyArray<Plan> = [...defaultPaymentPlans, ...paymentPlans];
  return (
    <Fragment>
      <Title className="mb-10" theme="title-brand-n2">
        {t('booking.banking.paymentSchedule.selectPaymentSchedule')}
      </Title>
      <Capsule header="none">
        <div className="p-20">
          {isLoading && <AsyncLoader />}
          {plans.map(
            ({
              onChange,
              value,
              selector: Selector = null,
              label,
              modalTitle = '',
              modalDescription = '',
              withModalInformation = false,
              template: { values, valuesClassName } = {},
              regulations = null,
              name,
            }) => {
              const fieldsToClear = [
                ...new Set(plans.filter((plan) => plan.name !== name).map((plan) => plan.name)),
              ];
              return (
                <div key={label} className={`${isLoading ? 'hidden' : 'block'} mt-10`}>
                  <PaymentFormikInput
                    type="radio"
                    name={`${name}`}
                    value={String(value)}
                    label={
                      Selector ? (
                        <Selector price={paymentSchedule.total?.value} label={label} />
                      ) : (
                        <span>
                          <TemplateString
                            template={t(label)}
                            values={values}
                            valuesClassName={valuesClassName}
                          />
                          {!!withModalInformation && (
                            <span
                              className="absolute"
                              onClick={(e) => {
                                e.preventDefault();
                                onOpen();
                              }}
                              role="button"
                              aria-hidden="true"
                            >
                              <Icon
                                name={ICONS.INFORMATION}
                                width="1.5rem"
                                className="text-sienna"
                              />
                            </span>
                          )}
                        </span>
                      )
                    }
                    onChange={() => {
                      fieldsToClear.map((field) => formik.setFieldValue(`${field}`, ''));
                      onChange && onChange();
                    }}
                    isDisabled={isDisabled}
                  />
                  {!!withModalInformation && isOpen && (
                    <Modal className="p-10" size="small" onClose={onClose}>
                      <div className="flex flex-col p-20">
                        <Text className="pb-20 font-bold text-sienna" text={t(modalTitle)} />
                        <div className="flex w-full flex-col">
                          <div className="flex flex-col">
                            <Text text={t(modalDescription)} />
                          </div>
                        </div>
                      </div>
                    </Modal>
                  )}
                  {regulations && regulations}
                </div>
              );
            },
          )}
        </div>
      </Capsule>
    </Fragment>
  );
};
